import { FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import type { DocumentRegisterEntry, DocumentRevision } from "@shared/types";
import { formatAud } from "@shared/types";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DashboardShell } from "../components/Layout";

const adminNav = [
  { to: "/admin", label: "Analytics" },
  { to: "/admin/submissions", label: "Submissions" },
  { to: "/admin/resources", label: "Resources" },
  { to: "/admin/document-register", label: "Document register" },
  { to: "/admin/enquiries", label: "Enquiries" },
  { to: "/admin/clients", label: "Clients" },
];

const DOC_KINDS = [
  "diy_template",
  "report_template",
  "checklist",
  "canvas",
  "procedure",
  "policy",
  "form",
  "capability",
  "profile",
] as const;

function RequireStaff({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <p className="container section muted">Loading…</p>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && user.role !== "reviewer") {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

export function AdminHome() {
  const [data, setData] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    void api.adminAnalytics().then(setData);
  }, []);

  return (
    <RequireStaff>
      <DashboardShell title="Admin" subtitle="Platform analytics and operations." nav={adminNav}>
        <div className="stat-grid">
          <div className="stat">
            <div className="label">Users</div>
            <div className="value">{data?.users ?? "—"}</div>
          </div>
          <div className="stat">
            <div className="label">Downloads</div>
            <div className="value">{data?.totalDownloads ?? "—"}</div>
          </div>
          <div className="stat">
            <div className="label">Paid orders</div>
            <div className="value">{data?.paidOrders ?? "—"}</div>
          </div>
          <div className="stat">
            <div className="label">Revenue incl. GST</div>
            <div className="value" style={{ fontSize: "1.1rem" }}>
              {data ? formatAud(Number(data.revenueCents || 0)) : "—"}
            </div>
          </div>
        </div>
        <div className="grid-2">
          <div className="card">
            <h3>Submissions by status</h3>
            <pre style={{ margin: 0, fontSize: "0.85rem" }}>
              {JSON.stringify(data?.submissionsByStatus ?? {}, null, 2)}
            </pre>
          </div>
          <div className="card">
            <h3>New enquiries</h3>
            <p className="price">{data?.newEnquiries ?? "—"}</p>
            <Link className="btn btn-outline btn-sm" to="/admin/enquiries">
              Review enquiries
            </Link>
          </div>
        </div>
      </DashboardShell>
    </RequireStaff>
  );
}

export function AdminSubmissions() {
  const [subs, setSubs] = useState<any[]>([]);

  useEffect(() => {
    void api.submissions().then((r) => setSubs(r.submissions));
  }, []);

  return (
    <RequireStaff>
      <DashboardShell title="Submissions queue" subtitle="Tier 2 review workflow." nav={adminNav}>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Project</th>
                <th>Type</th>
                <th>Status</th>
                <th>Submitted</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id}>
                  <td>{s.projectName || "—"}</td>
                  <td>{s.reportTypeName}</td>
                  <td>
                    <span className={`badge badge-status ${s.status}`}>
                      {s.status.replace("_", " ")}
                    </span>
                  </td>
                  <td>
                    {s.submittedAt
                      ? new Date(s.submittedAt).toLocaleString("en-AU")
                      : "—"}
                  </td>
                  <td>
                    <Link className="btn btn-outline btn-sm" to={`/admin/submissions/${s.id}`}>
                      Review
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DashboardShell>
    </RequireStaff>
  );
}

export function AdminSubmissionDetail() {
  const { id = "" } = useParams();
  const [sub, setSub] = useState<any>(null);
  const [notes, setNotes] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    const r = await api.submission(id);
    setSub(r.submission);
    setNotes(r.submission.reviewerNotes || "");
  }

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Failed"));
  }, [id]);

  async function setStatus(status: string) {
    setBusy(true);
    setError(null);
    try {
      await api.setSubmissionStatus(id, { status, reviewerNotes: notes });
      setMsg(`Status → ${status}`);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function generate() {
    setBusy(true);
    try {
      const res = await api.generatePdf(id);
      setMsg(`Generated ${res.fileName}`);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Generate failed");
    } finally {
      setBusy(false);
    }
  }

  if (!sub) {
    return (
      <RequireStaff>
        <DashboardShell title="Review" nav={adminNav}>
          <p className="muted">{error || "Loading…"}</p>
        </DashboardShell>
      </RequireStaff>
    );
  }

  return (
    <RequireStaff>
      <DashboardShell
        title={`Review · ${sub.reportType?.name || "Submission"}`}
        subtitle={sub.projectName || sub.id}
        nav={adminNav}
      >
        {msg ? <div className="alert alert-success">{msg}</div> : null}
        {error ? <div className="alert alert-error">{error}</div> : null}
        <div className="grid-2">
          <div className="card">
            <h3>Questionnaire answers</h3>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}>
              {JSON.stringify(sub.answers, null, 2)}
            </pre>
            <h3>Files</h3>
            <ul>
              {(sub.files || []).map((f: any) => (
                <li key={f.id}>
                  <a href={`/api/submissions/${id}/files/${f.id}`}>{f.file_name}</a>{" "}
                  <span className="muted">({f.kind})</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card">
            <label>
              Reviewer notes
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                className="btn btn-outline btn-sm"
                type="button"
                disabled={busy}
                onClick={() => void setStatus("in_review")}
              >
                Mark in review
              </button>
              <button
                className="btn btn-outline btn-sm"
                type="button"
                disabled={busy}
                onClick={() => void generate()}
              >
                Generate co-branded PDF (HTML)
              </button>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                disabled={busy}
                onClick={() => void setStatus("ready")}
              >
                Release to client
              </button>
            </div>
            {sub.finalFileName ? (
              <p style={{ marginTop: "1rem" }}>
                Deliverable:{" "}
                <button
                  className="btn btn-outline btn-sm"
                  type="button"
                  onClick={() => void api.downloadSubmission(id, sub.finalFileName)}
                >
                  {sub.finalFileName}
                </button>
              </p>
            ) : null}
          </div>
        </div>
      </DashboardShell>
    </RequireStaff>
  );
}

export function AdminResources() {
  const [resources, setResources] = useState<any[]>([]);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    summary: "",
    priceCents: 0,
    isFree: true,
  });
  const [msg, setMsg] = useState<string | null>(null);

  async function load() {
    const r = await api.adminResources();
    setResources(r.resources);
  }

  useEffect(() => {
    void load();
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    await api.createResource({
      title: form.title,
      slug: form.slug,
      summary: form.summary,
      priceCents: form.isFree ? 0 : form.priceCents,
      isFree: form.isFree,
    });
    setMsg("Resource created");
    setForm({ title: "", slug: "", summary: "", priceCents: 0, isFree: true });
    await load();
  }

  async function upload(id: string, file: File | null) {
    if (!file) return;
    await api.uploadResourceFile(id, file);
    setMsg(`Uploaded file for ${id}`);
    await load();
  }

  return (
    <RequireStaff>
      <DashboardShell title="Manage resources" nav={adminNav}>
        {msg ? <div className="alert alert-success">{msg}</div> : null}
        <form className="card" onSubmit={(e) => void create(e)} style={{ marginBottom: "1rem" }}>
          <h3>Add resource</h3>
          <div className="form-row">
            <label>
              Title
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label>
              Slug
              <input
                required
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </label>
          </div>
          <label>
            Summary
            <input
              required
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
            />
          </label>
          <div className="form-row">
            <label>
              Price cents (AUD resources: incl. GST)
              <input
                type="number"
                min={0}
                value={form.priceCents}
                onChange={(e) =>
                  setForm({ ...form, priceCents: Number(e.target.value), isFree: Number(e.target.value) === 0 })
                }
              />
            </label>
            <label>
              Free?
              <select
                value={form.isFree ? "1" : "0"}
                onChange={(e) => setForm({ ...form, isFree: e.target.value === "1" })}
              >
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </label>
          </div>
          <button className="btn btn-primary btn-sm" type="submit">
            Create
          </button>
        </form>

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Title</th>
                <th>Price</th>
                <th>Downloads</th>
                <th>File</th>
                <th>Upload</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((r) => (
                <tr key={r.id}>
                  <td>{r.title}</td>
                  <td>{r.is_free ? "Free" : formatAud(r.price_cents)}</td>
                  <td>{r.download_count}</td>
                  <td>{r.file_name || "—"}</td>
                  <td>
                    <input
                      type="file"
                      onChange={(e) => void upload(r.id, e.target.files?.[0] ?? null)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DashboardShell>
    </RequireStaff>
  );
}

export function AdminEnquiries() {
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    void api.adminEnquiries().then((r) => setRows(r.enquiries));
  }, []);

  return (
    <RequireStaff>
      <DashboardShell title="Enquiries" subtitle="Tier 3 consultancy intake." nav={adminNav}>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Service</th>
                <th>Status</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td>{e.full_name}</td>
                  <td>{e.email}</td>
                  <td>{e.service_interest || "—"}</td>
                  <td>{e.status}</td>
                  <td style={{ maxWidth: 280 }}>{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DashboardShell>
    </RequireStaff>
  );
}

export function AdminClients() {
  const [rows, setRows] = useState<any[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === "admin") {
      void api.adminClients().then((r) => setRows(r.clients));
    }
  }, [user]);

  if (user?.role !== "admin") {
    return (
      <RequireStaff>
        <DashboardShell title="Clients" nav={adminNav}>
          <p className="muted">Admin role required.</p>
        </DashboardShell>
      </RequireStaff>
    );
  }

  return (
    <RequireStaff>
      <DashboardShell title="Clients" nav={adminNav}>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Role</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>{c.full_name}</td>
                  <td>{c.email}</td>
                  <td>{c.company_name || "—"}</td>
                  <td>{c.role}</td>
                  <td>{new Date(c.created_at).toLocaleDateString("en-AU")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DashboardShell>
    </RequireStaff>
  );
}

function statusBadge(status: string) {
  const tone =
    status === "issued" || status === "active"
      ? "badge-free"
      : status === "draft" || status === "in_review"
        ? "badge-paid"
        : "badge-status";
  return <span className={`badge ${tone}`}>{status.replace(/_/g, " ")}</span>;
}

export function AdminDocumentRegister() {
  const { user } = useAuth();
  const [docs, setDocs] = useState<DocumentRegisterEntry[]>([]);
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    docId: "EE-DOC-",
    title: "",
    kind: "diy_template",
    domainCode: "",
    notes: "",
    revision: "0.1",
    issueDate: new Date().toISOString().slice(0, 10),
    changeSummary: "Initial controlled template registration.",
    fileName: "",
    issueNow: false,
  });

  async function load() {
    try {
      const r = await api.documentRegister({
        status: status || undefined,
        kind: kind || undefined,
        q: q || undefined,
      });
      setDocs(r.documents);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load register");
    }
  }

  useEffect(() => {
    void load();
  }, [status, kind]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    setErr(null);
    try {
      await api.createDocumentRegister({
        docId: form.docId,
        title: form.title,
        kind: form.kind,
        domainCode: form.domainCode || null,
        notes: form.notes || null,
        initialRevision: {
          revision: form.revision,
          issueDate: form.issueDate,
          changeSummary: form.changeSummary,
          fileName: form.fileName || null,
          issueNow: form.issueNow,
        },
      });
      setMsg(`Registered ${form.docId.toUpperCase()}`);
      setForm({
        docId: "EE-DOC-",
        title: "",
        kind: "diy_template",
        domainCode: "",
        notes: "",
        revision: "0.1",
        issueDate: new Date().toISOString().slice(0, 10),
        changeSummary: "Initial controlled template registration.",
        fileName: "",
        issueNow: false,
      });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Create failed");
    }
  }

  async function exportJson() {
    try {
      const data = await api.exportDocumentRegister();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ecoeng-document-register-${data.exportedAt.slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg("Register exported");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Export failed");
    }
  }

  return (
    <RequireStaff>
      <DashboardShell
        title="Document register"
        subtitle="Internal template & version control (ISO 9001 / 14001 / 45001 principles). Staff only."
        nav={adminNav}
      >
        {msg ? <div className="alert alert-success">{msg}</div> : null}
        {err ? <div className="alert alert-error">{err}</div> : null}

        <div className="card" style={{ marginBottom: "1rem" }}>
          <p className="muted" style={{ margin: "0 0 0.75rem" }}>
            Track controlled Doc IDs, revisions and supersession. Pricing stays on the public
            Resources shop — not on forms. Capability statement and company profile IDs are
            reserved for future procurement downloads.
          </p>
          <div className="form-row" style={{ alignItems: "flex-end" }}>
            <label>
              Status
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="withdrawn">Withdrawn</option>
                <option value="archived">Archived</option>
              </select>
            </label>
            <label>
              Kind
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="">All</option>
                {DOC_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ flex: 1 }}>
              Search
              <input
                value={q}
                placeholder="Doc ID or title"
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void load();
                }}
              />
            </label>
            <button className="btn btn-outline btn-sm" type="button" onClick={() => void load()}>
              Search
            </button>
            <button className="btn btn-outline btn-sm" type="button" onClick={() => void exportJson()}>
              Export JSON
            </button>
          </div>
        </div>

        <div className="table-wrap" style={{ marginBottom: "1.25rem" }}>
          <table className="data">
            <thead>
              <tr>
                <th>Doc ID</th>
                <th>Title</th>
                <th>Kind</th>
                <th>Rev</th>
                <th>Status</th>
                <th>Issue</th>
                <th>Linked resource</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td>
                    <code>{d.docId}</code>
                  </td>
                  <td>{d.title}</td>
                  <td>{String(d.kind).replace(/_/g, " ")}</td>
                  <td>{d.currentRevision || "—"}</td>
                  <td>{statusBadge(d.status)}</td>
                  <td>{d.currentIssueDate || "—"}</td>
                  <td>{d.resourceTitle || "—"}</td>
                  <td>
                    <Link className="btn btn-outline btn-sm" to={`/admin/document-register/${d.id}`}>
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
              {!docs.length ? (
                <tr>
                  <td colSpan={8} className="muted">
                    No documents match filters. Apply migration 0003 if the register is empty.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {user?.role === "admin" ? (
          <form className="card" onSubmit={(e) => void create(e)}>
            <h3>Register new controlled document</h3>
            <div className="form-row">
              <label>
                Doc ID
                <input
                  required
                  value={form.docId}
                  onChange={(e) => setForm({ ...form, docId: e.target.value })}
                  placeholder="EE-DOC-XX-001"
                />
              </label>
              <label>
                Kind
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                >
                  {DOC_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Domain code
                <input
                  value={form.domainCode}
                  onChange={(e) => setForm({ ...form, domainCode: e.target.value })}
                  placeholder="MP, BC, ASS…"
                />
              </label>
            </div>
            <label>
              Title
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label>
              Notes (internal)
              <input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </label>
            <div className="form-row">
              <label>
                Initial revision
                <input
                  required
                  value={form.revision}
                  onChange={(e) => setForm({ ...form, revision: e.target.value })}
                />
              </label>
              <label>
                Issue date
                <input
                  required
                  type="date"
                  value={form.issueDate}
                  onChange={(e) => setForm({ ...form, issueDate: e.target.value })}
                />
              </label>
              <label>
                File name
                <input
                  value={form.fileName}
                  onChange={(e) => setForm({ ...form, fileName: e.target.value })}
                  placeholder="Optional.pdf"
                />
              </label>
            </div>
            <label>
              Change summary
              <input
                required
                value={form.changeSummary}
                onChange={(e) => setForm({ ...form, changeSummary: e.target.value })}
              />
            </label>
            <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={form.issueNow}
                onChange={(e) => setForm({ ...form, issueNow: e.target.checked })}
              />
              Issue initial revision immediately (otherwise leave as draft)
            </label>
            <button className="btn btn-primary btn-sm" type="submit">
              Create document
            </button>
          </form>
        ) : (
          <p className="muted">Only admins can create new document masters. Reviewers can open and issue revisions.</p>
        )}
      </DashboardShell>
    </RequireStaff>
  );
}

export function AdminDocumentRegisterDetail() {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const [document, setDocument] = useState<DocumentRegisterEntry | null>(null);
  const [revisions, setRevisions] = useState<DocumentRevision[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [revForm, setRevForm] = useState({
    revision: "",
    issueDate: new Date().toISOString().slice(0, 10),
    changeSummary: "",
    fileName: "",
    pageCount: "",
    generatorScript: "",
  });
  const [notes, setNotes] = useState("");
  const [docStatus, setDocStatus] = useState("active");

  async function load() {
    if (!id) return;
    try {
      const r = await api.documentRegisterEntry(id);
      setDocument(r.document);
      setRevisions(r.revisions);
      setNotes(r.document.notes || "");
      setDocStatus(r.document.status);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load document");
    }
  }

  useEffect(() => {
    void load();
  }, [id]);

  async function addRevision(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      await api.addDocumentRevision(id, {
        revision: revForm.revision,
        issueDate: revForm.issueDate,
        changeSummary: revForm.changeSummary,
        fileName: revForm.fileName || null,
        pageCount: revForm.pageCount ? Number(revForm.pageCount) : null,
        generatorScript: revForm.generatorScript || null,
      });
      setMsg(`Draft revision ${revForm.revision} created`);
      setRevForm({
        revision: "",
        issueDate: new Date().toISOString().slice(0, 10),
        changeSummary: "",
        fileName: document?.currentFileName || "",
        pageCount: "",
        generatorScript: "",
      });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not add revision");
    } finally {
      setBusy(false);
    }
  }

  async function issue(revId: string) {
    if (!id) return;
    if (!confirm("Issue this revision? The previous current revision will be marked superseded.")) {
      return;
    }
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      await api.issueDocumentRevision(id, revId);
      setMsg("Revision issued");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Issue failed");
    } finally {
      setBusy(false);
    }
  }

  async function saveMaster(e: FormEvent) {
    e.preventDefault();
    if (!id || user?.role !== "admin") return;
    setBusy(true);
    try {
      await api.updateDocumentRegister(id, { notes, status: docStatus });
      setMsg("Document updated");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  if (!document && !err) {
    return (
      <RequireStaff>
        <DashboardShell title="Document" nav={adminNav}>
          <p className="muted">Loading…</p>
        </DashboardShell>
      </RequireStaff>
    );
  }

  return (
    <RequireStaff>
      <DashboardShell
        title={document?.docId || "Document"}
        subtitle={document?.title}
        nav={adminNav}
      >
        <p style={{ marginBottom: "1rem" }}>
          <Link className="btn btn-outline btn-sm" to="/admin/document-register">
            ← Back to register
          </Link>
        </p>
        {msg ? <div className="alert alert-success">{msg}</div> : null}
        {err ? <div className="alert alert-error">{err}</div> : null}

        {document ? (
          <>
            <div className="grid-2" style={{ marginBottom: "1rem" }}>
              <div className="card">
                <h3>Document control</h3>
                <dl className="kv">
                  <div>
                    <dt>Doc ID</dt>
                    <dd>
                      <code>{document.docId}</code>
                    </dd>
                  </div>
                  <div>
                    <dt>Kind</dt>
                    <dd>{String(document.kind).replace(/_/g, " ")}</dd>
                  </div>
                  <div>
                    <dt>Domain</dt>
                    <dd>{document.domainCode || "—"}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{statusBadge(document.status)}</dd>
                  </div>
                  <div>
                    <dt>Current rev</dt>
                    <dd>{document.currentRevisionId ? revisions.find((r) => r.id === document.currentRevisionId)?.revision || "—" : "—"}</dd>
                  </div>
                  <div>
                    <dt>Linked resource</dt>
                    <dd>
                      {document.resourceSlug ? (
                        <Link to={`/resources/${document.resourceSlug}`}>{document.resourceTitle}</Link>
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>
                </dl>
                <p className="muted" style={{ fontSize: "0.85rem", marginTop: "0.75rem" }}>
                  {document.classification}
                </p>
              </div>

              {user?.role === "admin" ? (
                <form className="card" onSubmit={(e) => void saveMaster(e)}>
                  <h3>Master (admin)</h3>
                  <label>
                    Status
                    <select value={docStatus} onChange={(e) => setDocStatus(e.target.value)}>
                      <option value="draft">draft</option>
                      <option value="active">active</option>
                      <option value="withdrawn">withdrawn</option>
                      <option value="archived">archived</option>
                    </select>
                  </label>
                  <label>
                    Internal notes
                    <textarea
                      rows={4}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </label>
                  <button className="btn btn-primary btn-sm" type="submit" disabled={busy}>
                    Save
                  </button>
                </form>
              ) : (
                <div className="card">
                  <h3>Notes</h3>
                  <p className="muted">{document.notes || "No internal notes."}</p>
                </div>
              )}
            </div>

            <div className="card" style={{ marginBottom: "1rem" }}>
              <h3>Revision history</h3>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Rev</th>
                      <th>Issue date</th>
                      <th>Status</th>
                      <th>Change summary</th>
                      <th>File</th>
                      <th>Approved</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {revisions.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <strong>{r.revision}</strong>
                          {document.currentRevisionId === r.id ? (
                            <span className="badge badge-free" style={{ marginLeft: 6 }}>
                              current
                            </span>
                          ) : null}
                        </td>
                        <td>{r.issueDate}</td>
                        <td>{statusBadge(r.status)}</td>
                        <td style={{ maxWidth: 320 }}>{r.changeSummary}</td>
                        <td>{r.fileName || "—"}</td>
                        <td>
                          {r.approvedBy
                            ? `${r.approvedBy}${r.approvedAt ? ` · ${r.approvedAt}` : ""}`
                            : "—"}
                        </td>
                        <td>
                          {r.status === "draft" || r.status === "in_review" || r.status === "approved" ? (
                            <button
                              className="btn btn-primary btn-sm"
                              type="button"
                              disabled={busy}
                              onClick={() => void issue(r.id)}
                            >
                              Issue
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                    {!revisions.length ? (
                      <tr>
                        <td colSpan={7} className="muted">
                          No revisions yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>

            <form className="card" onSubmit={(e) => void addRevision(e)}>
              <h3>Add draft revision</h3>
              <p className="muted" style={{ marginTop: 0 }}>
                Issued revisions are immutable. Create a new revision number, then Issue when ready.
              </p>
              <div className="form-row">
                <label>
                  Revision
                  <input
                    required
                    value={revForm.revision}
                    onChange={(e) => setRevForm({ ...revForm, revision: e.target.value })}
                    placeholder="1.2"
                  />
                </label>
                <label>
                  Issue date
                  <input
                    required
                    type="date"
                    value={revForm.issueDate}
                    onChange={(e) => setRevForm({ ...revForm, issueDate: e.target.value })}
                  />
                </label>
                <label>
                  File name
                  <input
                    value={revForm.fileName}
                    onChange={(e) => setRevForm({ ...revForm, fileName: e.target.value })}
                  />
                </label>
                <label>
                  Page count
                  <input
                    type="number"
                    min={0}
                    value={revForm.pageCount}
                    onChange={(e) => setRevForm({ ...revForm, pageCount: e.target.value })}
                  />
                </label>
              </div>
              <label>
                Change summary
                <input
                  required
                  value={revForm.changeSummary}
                  onChange={(e) => setRevForm({ ...revForm, changeSummary: e.target.value })}
                  placeholder="What changed in this revision?"
                />
              </label>
              <label>
                Generator script (optional)
                <input
                  value={revForm.generatorScript}
                  onChange={(e) => setRevForm({ ...revForm, generatorScript: e.target.value })}
                  placeholder="scripts/generate_resource_pdfs.py#…"
                />
              </label>
              <button className="btn btn-primary btn-sm" type="submit" disabled={busy}>
                Create draft revision
              </button>
            </form>
          </>
        ) : null}
      </DashboardShell>
    </RequireStaff>
  );
}
